/**
 * CP Admin 2077 Theme - Component Library Edition
 *
 * This theme file consolidates existing CP 2077 design tokens into
 * a TypeScript-first format for the component library. It references
 * values from the existing design system to ensure consistency.
 *
 * @see design-system/tokens/colors.ts - Source of truth for color values
 * @see src/styles/theme.module.css - CSS Module implementation
 * @see apps/web-vite/src/lib/orange-theme.ts - Tailwind utilities
 */

// ============================================================================
// COLORS - CP Admin 2077 Palette
// ============================================================================

export const COLORS = {
  // Primary Orange (Brand Color)
  primary: {
    orange: '#FF6B35',      // Main brand orange (HSL: 24.6 95% 53.1%)
    orangeLight: '#FF9B50', // Light orange (hover states)
    orangeDark: '#D9440F',  // Dark orange (gradients)
    cyan: '#00F0FF',        // Secondary accent
    purple: '#B91BFF',      // Tertiary accent
    yellow: '#FFD93D',      // Quaternary accent
  },

  // Status Colors
  status: {
    success: '#6BCF7F',
    warning: '#FFA500',
    error: '#FF0000',
    info: '#00F0FF',
    active: '#00FF00',
    inactive: '#8A95AA',
  },

  // Text Colors
  text: {
    primary: '#E0E6F0',     // Light gray-white
    secondary: '#8A95AA',   // Medium gray
    tertiary: '#626F86',    // Darker gray
    disabled: '#4A5568',    // Disabled text
    accent: '#FF9B50',      // Orange accent text
  },

  // Background Colors
  background: {
    primary: '#0A0E15',     // Darkest (page background)
    secondary: '#1A1F2E',   // Medium (card background start)
    tertiary: '#242938',    // Lightest (card background end)
    elevated: 'linear-gradient(135deg, #1A1F2E, #242938)', // Card gradient
    input: '#334155',       // Input field background
  },

  // Border Colors
  border: {
    default: '#2D3748',
    focus: '#FF6B35',
    error: '#FF0000',
    success: '#6BCF7F',
  },
} as const

// ============================================================================
// SPACING - Consistent Spacing Scale
// ============================================================================

export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '32px',
  xxxl: '48px',
} as const

// ============================================================================
// BORDER RADIUS - CP 2077 Sharp Aesthetic
// ============================================================================

export const BORDER_RADIUS = {
  none: '0px',        // Cards, buttons (default for CP 2077)
  sm: '4px',
  md: '6px',
  lg: '8px',          // Inputs only
  xl: '12px',
  full: '9999px',
} as const

// ============================================================================
// SHADOWS - Glow Effects
// ============================================================================

/**
 * Generate box-shadow glow effect
 * @param color - RGB values as string (e.g., "255, 107, 53")
 * @param opacity - Opacity for outer glow (0-1)
 */
export const SHADOWS = {
  // Standard glow (cards, default state)
  sm: (color: string, opacity: number = 0.2) =>
    `0 0 10px rgba(${color}, ${opacity})`,

  // Medium glow (cards hover, buttons default)
  md: (color: string, opacity: number = 0.3) =>
    `0 0 20px rgba(${color}, ${opacity}), inset 0 0 20px rgba(${color}, ${opacity * 0.2})`,

  // Large glow (buttons hover, active states)
  lg: (color: string, opacity: number = 0.5) =>
    `0 0 30px rgba(${color}, ${opacity}), inset 0 0 30px rgba(${color}, ${opacity * 0.2})`,

  // Extra large glow (dramatic emphasis)
  xl: (color: string, opacity: number = 0.8) =>
    `0 0 40px rgba(${color}, ${opacity})`,

  // Focus ring (inputs)
  focus: (color: string, opacity: number = 0.2) =>
    `0 0 0 3px rgba(${color}, ${opacity})`,
} as const

// Pre-defined shadow values for common usage
export const SHADOW_PRESETS = {
  cardDefault: `0 0 20px rgba(255, 107, 53, 0.3), inset 0 0 20px rgba(255, 107, 53, 0.05)`,
  cardHover: `0 0 30px rgba(255, 107, 53, 0.5), inset 0 0 30px rgba(255, 107, 53, 0.1)`,
  buttonDefault: `0 0 15px rgba(255, 107, 53, 0.5)`,
  buttonHover: `0 0 25px rgba(255, 107, 53, 0.8)`,
  inputFocus: `0 0 0 3px rgba(255, 107, 53, 0.2)`,
} as const

// ============================================================================
// TYPOGRAPHY - CP 2077 Fonts
// ============================================================================

export const TYPOGRAPHY = {
  fontFamily: {
    primary: "'Rajdhani', sans-serif",   // Headings, buttons, labels
    body: "'Inter', sans-serif",          // Body text
    mono: "'Courier New', monospace",     // Code, monospace
  },

  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    xxl: '1.5rem',    // 24px
    xxxl: '2rem',     // 32px
    xxxxl: '3rem',    // 48px
  },

  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',    // Card titles
    widest: '0.1em',    // Buttons, headings
  },

  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const

// ============================================================================
// ANIMATIONS - CP 2077 Effects
// ============================================================================

export const ANIMATIONS = {
  // Transition timings
  transition: {
    fast: '0.2s ease',      // Inputs, quick interactions
    normal: '0.3s ease',    // Cards, buttons, standard interactions
    slow: '0.5s ease',      // Page transitions
  },

  // Scanline animation
  scanline: {
    duration: '2s',
    timing: 'linear',
    iteration: 'infinite',
  },

  // Hover effects
  hover: {
    lift: 'translateY(-2px)',    // Cards
    scale: 'scale(1.02)',        // Buttons
    press: 'scale(0.98)',        // Active state
  },
} as const

// ============================================================================
// GRADIENTS - Background Patterns
// ============================================================================

export const GRADIENTS = {
  // Card backgrounds
  card: 'linear-gradient(135deg, #1A1F2E, #242938)',

  // Button backgrounds
  buttonPrimary: 'linear-gradient(90deg, #D9440F, #FF6B35)',

  // Page background grid pattern
  pageGrid: `
    repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 107, 53, 0.03) 2px, rgba(255, 107, 53, 0.03) 4px),
    repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255, 107, 53, 0.03) 2px, rgba(255, 107, 53, 0.03) 4px)
  `,

  // Card scanlines
  scanlines: `
    repeating-linear-gradient(0deg, transparent, transparent 8px, rgba(255, 255, 255, 0.02) 8px, rgba(255, 255, 255, 0.02) 10px)
  `,
} as const

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert hex color to RGB string for rgba() usage
 * @param hex - Hex color (e.g., "#FF6B35")
 * @returns RGB string (e.g., "255, 107, 53")
 */
export const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '0, 0, 0'
}

/**
 * Generate rgba color string
 * @param hex - Hex color
 * @param alpha - Opacity (0-1)
 */
export const rgba = (hex: string, alpha: number): string => {
  return `rgba(${hexToRgb(hex)}, ${alpha})`
}

/**
 * Get orange RGB for shadow functions
 */
export const ORANGE_RGB = hexToRgb(COLORS.primary.orange) // "255, 107, 53"

// ============================================================================
// MANAGEMENT AREA GLOW COLORS (AdminPage-2077 style)
// ============================================================================

export const MANAGEMENT_GLOW_COLORS = [
  { name: 'cyan', color: '#00F0FF', rgb: hexToRgb('#00F0FF') },
  { name: 'orange', color: '#FF6B35', rgb: hexToRgb('#FF6B35') },
  { name: 'purple', color: '#B91BFF', rgb: hexToRgb('#B91BFF') },
  { name: 'yellow', color: '#FFD93D', rgb: hexToRgb('#FFD93D') },
  { name: 'green', color: '#6BCF7F', rgb: hexToRgb('#6BCF7F') },
  { name: 'amber', color: '#FFA500', rgb: hexToRgb('#FFA500') },
  { name: 'pink', color: '#FF1493', rgb: hexToRgb('#FF1493') },
] as const

// ============================================================================
// TYPE EXPORTS
// ============================================================================

// ============================================================================
// FINTECH PALETTE - EscapeRoute (Personal Finance App)
// ============================================================================
// Source: UI-UX Pro Max Skill - Fintech/Crypto palette (row 15)
// Design Intent: Dark tech colors with trust-building golds and vibrant purple
// accents for user actions. The deep background conveys security and stability.
// Use Cases: Budget tracking, debt management, investment monitoring, financial reports

export const FINTECH_PALETTE = {
  primary: '#F59E0B',       // Warm Gold - trust, premium feel
  secondary: '#FBBF24',     // Light Gold - highlights, accents
  tertiary: '#8B5CF6',      // Purple - emphasis, user actions
  background: '#0F172A',    // Deep Blue-Black - professional, secure
  text: '#F8FAFC',          // Off-white - high contrast, readability
  border: '#334155',        // Dark Grey - subtle divisions
} as const

// ============================================================================
// E-COMMERCE PALETTE - BGS Core (Inventory Management & Sales)
// ============================================================================
// Source: UI-UX Pro Max Skill - E-commerce palette (row 3)
// Design Intent: Brand primary blue with orange CTAs for clear action hierarchy.
// Light backgrounds provide clarity for product information and sales data.
// Use Cases: Product listings, inventory management, order tracking, sales dashboards

export const ECOMMERCE_PALETTE = {
  primary: '#3B82F6',       // Blue - trust, professionalism, brand color
  secondary: '#60A5FA',     // Light Blue - supporting elements, secondary actions
  cta: '#F97316',           // Orange - high-contrast call-to-action buttons
  background: '#F8FAFC',    // Off-white - clean, open, spacious
  text: '#1E293B',          // Dark Slate - readable, accessible, high contrast
  border: '#E2E8F0',        // Light Grey - subtle separation, dividers
} as const

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type ThemeColor = keyof typeof COLORS
export type SpacingSize = keyof typeof SPACING
export type BorderRadiusSize = keyof typeof BORDER_RADIUS
export type FontSize = keyof typeof TYPOGRAPHY.fontSize
export type FontWeight = keyof typeof TYPOGRAPHY.fontWeight
